import { createFileRoute } from "@tanstack/react-router";

import { ServicePageLayout } from "../components/site/ServicePageLayout";
import { ClientsMarquee } from "../components/shared/ClientsMarquee";
import { ContactForm } from "../components/home/ContactForm";
import { Reviews } from "../components/shared/Reviews";
import { Faq } from "../components/shared/Faq";
import { SellerHero } from "../components/seller/SellerHero";
import { SellerMarket } from "../components/seller/SellerMarket";
import { SellerHybrid } from "../components/seller/SellerHybrid";
import { SellerBenefits } from "../components/seller/SellerBenefits";
import { SellerEconomics } from "../components/seller/SellerEconomics";
import { SellerProcess } from "../components/seller/SellerProcess";
import { SellerOffer } from "../components/seller/SellerOffer";
import { buildMeta, breadcrumbJsonLd, serviceJsonLd, SITE_URL } from "../lib/site";

const path = "/svoj-internet-magazin-dlya-sellerov";
const title =
  "Свой интернет-магазин для продавцов Wildberries и Ozon | HAY.agency";
const description =
  "Запускаем собственный канал продаж для селлеров: интернет-магазин, перенос каталога, оплата и доставка, CRM, аналитика и маркетинг. Маркетплейсы остаются каналом продаж.";

const faqItems = [
  {
    q: "Нужно ли уходить с Wildberries и Ozon?",
    a: "Нет. Мы рассматриваем маркетплейсы как отдельный канал продаж, который может работать параллельно с собственным интернет-магазином. Задача — снизить зависимость от одного канала, а не отказаться от площадок.",
  },
  {
    q: "Можно ли перенести товары с маркетплейса в свой магазин?",
    a: "Мы переносим и структурируем доступные данные о товарах: описания, характеристики, изображения и цены из учётной системы, выгрузок и файлов продавца. Закрытые внутренние данные площадок при этом не используются.",
  },
  {
    q: "Как синхронизировать остатки и цены?",
    a: "Синхронизация настраивается через учётную систему или CRM — например 1С, МойСклад или RetailCRM. Состав интеграций определяем после аудита текущей инфраструктуры.",
  },
  {
    q: "Откуда пойдёт трафик на собственный сайт?",
    a: "Основные источники — SEO, Яндекс Директ, ретаргетинг, CRM-маркетинг и собственная аудитория бренда. План по каналам формируем исходя из экономики проекта.",
  },
  {
    q: "Собственный сайт дешевле маркетплейса?",
    a: "Не всегда. Сайт требует инвестиций в разработку и трафик, но даёт контроль над каналом, клиентскую базу и повторные продажи. Поэтому мы сначала считаем экономику канала.",
  },
  {
    q: "Как уйти с Wildberries или Ozon, если это всё же нужно?",
    a: "Переход выполняется постепенно: сначала запускается собственный канал и накапливается трафик, затем доля продаж перераспределяется. Резкий отказ от площадок до выхода собственного магазина на стабильные продажи мы не рекомендуем.",
  },
  {
    q: "Сколько занимает запуск интернет-магазина?",
    a: "Запуск интернет-магазина занимает от 3,5 месяцев в зависимости от объёма каталога, интеграций и бизнес-логики. Точные сроки фиксируем после проектирования.",
  },
];

export const Route = createFileRoute("/svoj-internet-magazin-dlya-sellerov")({
  head: () => ({
    ...buildMeta({
      title,
      description,
      path,
      image: `${SITE_URL}/og/services/seller-own-store.webp`,
      imageWidth: 1200,
      imageHeight: 630,
      imageAlt:
        "Собственный интернет-магазин для продавцов Wildberries и Ozon — HAY.agency",
      locale: "ru_RU",
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              "@id": `${SITE_URL}${path}#webpage`,
              url: `${SITE_URL}${path}`,
              name: title,
              description,
              inLanguage: "ru-RU",
              isPartOf: { "@id": `${SITE_URL}/#website` },
              breadcrumb: { "@id": `${SITE_URL}${path}#breadcrumbs` },
              mainEntity: { "@id": `${SITE_URL}${path}#service` },
            },
            serviceJsonLd({
              name: "Запуск собственного интернет-магазина для продавцов Wildberries и Ozon",
              description,
              path,
              id: `${SITE_URL}${path}#service`,
              serviceType:
                "Разработка интернет-магазина и запуск собственного канала продаж для селлеров",
              providerId: `${SITE_URL}/#organization`,
              inGraph: true,
            }),
            breadcrumbJsonLd(
              [
                ["Главная", "/"],
                ["Услуги", "/uslugi"],
                ["Интернет-магазины", "/ecom"],
                ["Для продавцов Wildberries и Ozon", path],
              ],
              { id: `${SITE_URL}${path}#breadcrumbs`, inGraph: true },
            ),
            {
              "@type": "FAQPage",
              "@id": `${SITE_URL}${path}#faq`,
              mainEntity: faqItems.map((item) => ({
                "@type": "Question",
                name: item.q,
                acceptedAnswer: { "@type": "Answer", text: item.a },
              })),
            },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout breadcrumb="Для продавцов Wildberries и Ozon" jsonLd={false}>
      <SellerHero />
      <ClientsMarquee bare />
      <SellerHybrid />
      <SellerMarket />
      <SellerBenefits />
      <SellerEconomics />
      <SellerProcess />
      <SellerOffer />
      <Reviews
        only={["Наала", "Рафаэл Манвелян", "Артем Авакимов", "Роберт Оксюзян"]}
      />
      <Faq
        compact
        title="Частые вопросы продавцов маркетплейсов"
        lead="Перенос каталога, остатки, трафик и экономика канала."
        items={faqItems}
      />
      <ContactForm
        formName="Селлеры — собственный интернет-магазин"
        formId="seller_own_store"
        title="Обсудим запуск собственного интернет-магазина"
      />
    </ServicePageLayout>
  );
}
